import type {
  CurrentUser,
  LoginRequest,
  LoginResponse,
  MeResponse,
} from '@lens/shared';
import { apiRequest, resetCsrfToken } from '../../shared/api/client.ts';

/**
 * Обёртки над эндпоинтами `/api/auth/*`. Выносим их отдельно, чтобы
 * AuthContext/LoginPage работали с доменными функциями, а не с сырым
 * `apiRequest`. Типы берутся напрямую из `@lens/shared` — это наш
 * контракт между backend и frontend.
 */

export async function loginRequest(input: LoginRequest): Promise<CurrentUser> {
  const res = await apiRequest<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: input,
    // На логин CSRF не нужен: у пользователя ещё нет сессии,
    // а значит и csrf-секрета в session store.
    csrf: false,
  });
  return res.user;
}

export async function meRequest(): Promise<MeResponse> {
  return apiRequest<MeResponse>('/api/auth/me', { method: 'GET' });
}

export async function logoutRequest(): Promise<void> {
  await apiRequest<void>('/api/auth/logout', { method: 'POST' });
  // Сессия сменилась → старый csrf-секрет больше не валиден.
  resetCsrfToken();
}

export interface SsoStatus {
  enabled: boolean;
  configured: boolean;
}

/**
 * Метка «человек сам нажал „Выйти“». Пока она стоит, `/login` не уводит
 * в AlfaProject сам: сессия Keycloak ещё жива, и автопереход тут же
 * впустил бы обратно. Живёт в sessionStorage, чтобы пережить перезагрузку.
 */
const LOGGED_OUT_KEY = 'lens.loggedOut';

export function markLoggedOut(): void {
  try {
    sessionStorage.setItem(LOGGED_OUT_KEY, '1');
  } catch {
    // приватный режим — без метки страница просто уйдёт в AlfaProject
  }
}

export function wasLoggedOut(): boolean {
  try {
    return sessionStorage.getItem(LOGGED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearLoggedOut(): void {
  try {
    sessionStorage.removeItem(LOGGED_OUT_KEY);
  } catch {
    // нечего чистить
  }
}

/** Публичный статус SSO — уводить ли `/login` сразу в AlfaProject. */
export async function ssoStatusRequest(): Promise<SsoStatus> {
  return apiRequest<SsoStatus>('/api/auth/sso/status', { method: 'GET', csrf: false });
}
